import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "../anim";
import { ArrowDownRight } from "lucide-react";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const blobRot = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-36 pb-40 md:pb-56 px-5 md:px-10"
    >
      {/* parallax blob */}
      <motion.div
        style={{ y: blobY, rotate: blobRot }}
        className="pointer-events-none absolute -right-24 top-24 md:top-16 w-[70vw] md:w-[42vw] aspect-square rounded-full bg-[#FF8473] blur-[80px] opacity-30"
      />
      <motion.img
        src="/rada-logo-white.png"
        alt=""
        aria-hidden
        style={{ y: blobY }}
        className="pointer-events-none absolute right-2 md:right-6 top-44 md:top-56 w-[54vw] md:w-[29vw] opacity-[0.05] select-none"
      />

      <div className="relative mx-auto max-w-[1600px]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8 md:mb-12"
        >
          <span className="h-2 w-2 rounded-full bg-[#D7E85C] animate-pulse" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#FF8473]">
            #fromFilipinostotheWorld
          </span>
        </motion.div>

        <motion.h1
          style={{ y: yBig }}
          className="font-display font-black uppercase tracking-tighter leading-[0.82] text-[15vw] sm:text-[13vw] md:text-[11vw] lg:text-[9.5vw]"
        >
          <MaskedLines
            testid="hero-heading"
            delay={0.25}
            lines={[
              <span key="1" className="text-[#EBE6E0]">Design for</span>,
              <span key="2" className="text-[#EBE6E0]">everyday.</span>,
              <span key="3" className="text-[#FF8473] italic">Crafted</span>,
              <span key="4" className="text-[#EBE6E0]">for impact.</span>,
            ]}
          />
        </motion.h1>

        <motion.div
          style={{ y: ySmall }}
          className="mt-10 md:mt-16 grid md:grid-cols-12 gap-8 items-end"
        >
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-subtext"
            className="md:col-span-7 lg:col-span-6 text-base md:text-xl leading-relaxed text-[#FFFFFF] max-w-2xl"
          >
            <span className="text-[#EBE6E0]">Rada Collab</span> is a digital-first creative
            powerhouse—built by design, crafted for impact. Creating a new product shouldn't be a
            struggle. Skip the guesswork. We design market-ready products made to stand out.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="md:col-span-5 lg:col-span-6 flex md:justify-end"
          >
            <button
              data-testid="hero-cta"
              onClick={() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 bg-[#EBE6E0] text-[#1F3864] pl-7 pr-3 py-3 rounded-full font-bold uppercase tracking-wide hover:bg-[#D7E85C] active:scale-95 transition-[background,transform] duration-200"
            >
              Create with us
              <span className="grid place-items-center h-10 w-10 rounded-full bg-[#1F3864] text-[#EBE6E0] group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={18} />
              </span>
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
